import { useState, useMemo } from "react";
import { Article, Plus, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { CopyButton } from "@/components/CopyButton";
import { useSeo } from "@/lib/seo";

const MAX = 5000;
const emptyChapter = () => ({ id: crypto.randomUUID(), time: "", label: "" });
const emptyLink = () => ({ id: crypto.randomUUID(), label: "", url: "" });

const PRESETS = {
  tutorial: {
    hook: "In this tutorial you'll learn exactly how to ",
    cta: "👍 If this helped, drop a like and subscribe for more.",
    chapters: [
      { id: crypto.randomUUID(), time: "0:00", label: "Intro" },
      { id: crypto.randomUUID(), time: "0:45", label: "What we're building" },
      { id: crypto.randomUUID(), time: "2:10", label: "Step 1" },
      { id: crypto.randomUUID(), time: "5:30", label: "Wrap up" },
    ],
    hashtags: "#tutorial #howto",
  },
  vlog: {
    hook: "Come along with me as I ",
    cta: "🎬 New vlogs every week — hit subscribe so you don't miss the next one.",
    chapters: [
      { id: crypto.randomUUID(), time: "0:00", label: "Morning" },
      { id: crypto.randomUUID(), time: "2:00", label: "The plan" },
      { id: crypto.randomUUID(), time: "5:00", label: "The chaos" },
    ],
    hashtags: "#vlog #dayinmylife",
  },
  educational: {
    hook: "By the end of this video you'll understand ",
    cta: "📚 Save this video for later and share it with someone who needs it.",
    chapters: [
      { id: crypto.randomUUID(), time: "0:00", label: "Intro" },
      { id: crypto.randomUUID(), time: "1:00", label: "Core concept" },
      { id: crypto.randomUUID(), time: "4:00", label: "Example" },
      { id: crypto.randomUUID(), time: "7:00", label: "Recap" },
    ],
    hashtags: "#learn #education",
  },
};

const buildDescription = (f) => {
  const parts = [];
  if (f.hook.trim()) parts.push(f.hook.trim());
  const chaps = f.chapters.filter((c) => c.time.trim() && c.label.trim());
  if (chaps.length) {
    parts.push("");
    parts.push("⏱ CHAPTERS");
    chaps.forEach((c) => parts.push(`${c.time} ${c.label}`));
  }
  const socials = f.socials.filter((l) => l.url.trim());
  if (socials.length) {
    parts.push("");
    parts.push("🔗 CONNECT");
    socials.forEach((l) => parts.push(`${l.label || "Link"}: ${l.url}`));
  }
  const affiliates = f.affiliates.filter((l) => l.url.trim());
  if (affiliates.length) {
    parts.push("");
    parts.push("🛒 GEAR & LINKS (some may be affiliate links)");
    affiliates.forEach((l) => parts.push(`${l.label || "Product"}: ${l.url}`));
  }
  if (f.cta.trim()) {
    parts.push("");
    parts.push(f.cta.trim());
  }
  if (f.hashtags.trim()) {
    parts.push("");
    parts.push(f.hashtags.trim());
  }
  return parts.join("\n");
};

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-[var(--muted)] font-semibold">{label}</span>
    <div className="mt-2">{children}</div>
    {hint && <p className="text-[11px] text-[var(--muted)] mt-1.5">{hint}</p>}
  </label>
);

const inputCls = "min-w-0 bg-[var(--bg)] border border-[var(--border-c)] rounded-[10px] px-3 py-2 text-sm outline-none focus:border-[var(--glow)]";

export default function DescriptionGenerator() {
  useSeo(
    "YouTube Description Generator",
    "Generate a clean, copy-paste YouTube video description with chapters, social links and affiliate links — with a live 5000-character counter."
  );
  const [form, setForm] = useState({
    hook: "",
    cta: "👍 Like & Subscribe if this helped!",
    hashtags: "",
    chapters: [emptyChapter()],
    socials: [{ id: crypto.randomUUID(), label: "Instagram", url: "" }],
    affiliates: [emptyLink()],
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const patchList = (key, id, patch) =>
    update({ [key]: form[key].map((x) => (x.id === id ? { ...x, ...patch } : x)) });
  const addRow = (key, factory) => update({ [key]: [...form[key], factory()] });
  const delRow = (key, id) => update({ [key]: form[key].filter((x) => x.id !== id) });

  const applyPreset = (name) => {
    const p = PRESETS[name];
    setForm((f) => ({
      ...f,
      hook: p.hook,
      cta: p.cta,
      chapters: p.chapters.map((c) => ({ ...c, id: crypto.randomUUID() })),
      hashtags: p.hashtags,
    }));
    api.post("/track", { tool: "description_generator" }).catch(() => {});
  };

  const output = useMemo(() => buildDescription(form), [form]);
  const chars = output.length;
  const isOver = chars > MAX;

  return (
    <ToolShell
      slug="description-generator"
      title="Description Generator"
      tagline="Build a clean, structured YouTube description in seconds — chapters, socials, affiliate links, hashtags."
      hasResults={chars > 0}
    >
      <div className="surface rounded-[20px] p-5 sm:p-8">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-xs uppercase tracking-widest text-[var(--muted)] mr-1">Quick preset:</span>
          {Object.keys(PRESETS).map((k) => (
            <button
              key={k}
              data-testid={`preset-${k}`}
              onClick={() => applyPreset(k)}
              className="px-3 py-1 rounded-full text-xs font-semibold border border-[var(--border-c)] hover:border-[var(--glow)] hover:text-[var(--glow)] transition-colors capitalize"
            >
              {k}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-5">
            <Field label="Intro / hook" hint="First 2–3 lines are what viewers see before 'Show more'.">
              <textarea
                data-testid="desc-hook"
                value={form.hook}
                onChange={(e) => update({ hook: e.target.value })}
                rows={3}
                placeholder="In this video I break down…"
                className={`w-full ${inputCls}`}
              />
            </Field>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-widest text-[var(--muted)] font-semibold">Chapters</span>
                <button data-testid="add-chapter" onClick={() => addRow("chapters", emptyChapter)} className="text-xs text-[var(--glow)] font-semibold inline-flex items-center gap-1">
                  <Plus size={13} weight="bold" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {form.chapters.map((c, i) => (
                  <div key={c.id} className="flex gap-2 min-w-0">
                    <input
                      data-testid={`chapter-time-${i}`}
                      value={c.time}
                      onChange={(e) => patchList("chapters", c.id, { time: e.target.value })}
                      placeholder="0:00"
                      className={`${inputCls} w-20 shrink-0`}
                    />
                    <input
                      data-testid={`chapter-label-${i}`}
                      value={c.label}
                      onChange={(e) => patchList("chapters", c.id, { label: e.target.value })}
                      placeholder="Intro"
                      className={`w-full ${inputCls} flex-1`}
                    />
                    <button
                      data-testid={`chapter-del-${i}`}
                      onClick={() => delRow("chapters", c.id)}
                      className="shrink-0 p-2 text-[var(--muted)] hover:text-[var(--coral)]"
                      aria-label="Remove chapter"
                    >
                      <Trash size={15} />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[var(--muted)] mt-2">First chapter must be 0:00. Need 3+ chapters for YouTube to show them.</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-widest text-[var(--muted)] font-semibold">Social links</span>
                <button data-testid="add-social" onClick={() => addRow("socials", emptyLink)} className="text-xs text-[var(--glow)] font-semibold inline-flex items-center gap-1">
                  <Plus size={13} weight="bold" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {form.socials.map((l, i) => (
                  <div key={l.id} className="flex gap-2 min-w-0">
                    <input
                      data-testid={`social-label-${i}`}
                      value={l.label}
                      onChange={(e) => patchList("socials", l.id, { label: e.target.value })}
                      placeholder="Instagram"
                      className={`${inputCls} w-24 sm:w-28 shrink-0`}
                    />
                    <input
                      data-testid={`social-url-${i}`}
                      value={l.url}
                      onChange={(e) => patchList("socials", l.id, { url: e.target.value })}
                      placeholder="https://instagram.com/you"
                      className={`w-full ${inputCls} flex-1`}
                    />
                    <button
                      data-testid={`social-del-${i}`}
                      onClick={() => delRow("socials", l.id)}
                      className="shrink-0 p-2 text-[var(--muted)] hover:text-[var(--coral)]"
                      aria-label="Remove social link"
                    >
                      <Trash size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-widest text-[var(--muted)] font-semibold">Affiliate / gear</span>
                <button data-testid="add-affiliate" onClick={() => addRow("affiliates", emptyLink)} className="text-xs text-[var(--glow)] font-semibold inline-flex items-center gap-1">
                  <Plus size={13} weight="bold" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {form.affiliates.map((l, i) => (
                  <div key={l.id} className="flex gap-2 min-w-0">
                    <input
                      data-testid={`affiliate-label-${i}`}
                      value={l.label}
                      onChange={(e) => patchList("affiliates", l.id, { label: e.target.value })}
                      placeholder="My mic"
                      className={`${inputCls} w-24 sm:w-28 shrink-0`}
                    />
                    <input
                      data-testid={`affiliate-url-${i}`}
                      value={l.url}
                      onChange={(e) => patchList("affiliates", l.id, { url: e.target.value })}
                      placeholder="https://…"
                      className={`w-full ${inputCls} flex-1`}
                    />
                    <button
                      data-testid={`affiliate-del-${i}`}
                      onClick={() => delRow("affiliates", l.id)}
                      className="shrink-0 p-2 text-[var(--muted)] hover:text-[var(--coral)]"
                      aria-label="Remove affiliate link"
                    >
                      <Trash size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <Field label="Call to action">
              <input data-testid="desc-cta" value={form.cta} onChange={(e) => update({ cta: e.target.value })} className={`w-full ${inputCls}`} />
            </Field>

            <Field label="Hashtags" hint="Up to 15. YouTube shows the first 3 above your title.">
              <input
                data-testid="desc-hashtags"
                value={form.hashtags}
                onChange={(e) => update({ hashtags: e.target.value })}
                placeholder="#youtube #creator"
                className={`w-full ${inputCls}`}
              />
            </Field>
          </div>

          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest text-[var(--muted)] font-semibold flex items-center gap-2">
                <Article size={13} weight="duotone" /> Preview
              </span>
              <span
                data-testid="desc-char-count"
                className={`text-xs font-mono ${isOver ? "text-[var(--coral)]" : "text-[var(--muted)]"}`}
              >
                {chars.toLocaleString()} / {MAX.toLocaleString()}
              </span>
            </div>
            <textarea
              data-testid="desc-output"
              readOnly
              value={output}
              className="flex-1 min-h-[420px] bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] p-4 text-sm font-mono leading-relaxed outline-none resize-none"
            />
            <div className="mt-3">
              <CopyButton text={output} label="Copy description" testId="desc-copy-btn" />
            </div>
          </div>
        </div>
      </div>
    </ToolShell>
  );
}
